/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Business, Product, UserData } from "../utils/types";

export interface DiscountCode {
  code: string;
  type: 'percentage' | 'fixed';
  amount: number;
  description?: string;
  stripePromoId?: string | null;
}

export interface AppSliceState {
  basketProduct: Business | null;
  userData: UserData;
  loading: boolean;
  businessProfile: any;
  selectedProduct: Product;
  allBusiness: any;
  userExist: boolean;
  firebaseUser: any;
  userMembershipStatus: any;
  membershipUpgraded: boolean;
  isNextGymSelected: boolean;
  businessDashboardData: any;
  partnerGymInfo: any;
  partnerGyms: any[];
  userCanDowngradeToBasic: boolean;
  clientSecretStripe: string;
  appliedDiscount: DiscountCode | null;
  discountError: string | null;
  subscriptionId: string | null;
}

const appDataInitialState: AppSliceState = {
  basketProduct: {
    businessId: "",
    name: "",
    type: "",
    description: "",
    gallery: [],
    amenities: [],
    address: { addressId: "", fullAddress: "" },
    socialLinks: [
      {
        name: "",
        socialLink: "",
      },
    ],
    products: [],
  },
  userData: {
    userId: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    currencyCode: "",
    address: "",
    role: "",
  },
  loading: false,
  businessProfile: {},
  selectedProduct: {
    businessProductId: "",
    businessId: "",
    name: "",
    preTaxCost: "",
    createdAt: "",
    price: {
      feeAmount: 0,
      preTaxCost: 0,
      taxAmount: 0,
      total: 0,
    },
    stripeProductId: "",
  },
  allBusiness: [],
  userExist: false,
  firebaseUser: {},
  userMembershipStatus: {},
  membershipUpgraded: false,
  isNextGymSelected: false,
  businessDashboardData: [],
  partnerGymInfo: {},
  partnerGyms: [],
  userCanDowngradeToBasic: false,
  clientSecretStripe: "",
  appliedDiscount: null,
  discountError: null,
  subscriptionId: null,
};

const appData = createSlice({
  name: "appData",
  initialState: appDataInitialState,
  reducers: {
    setUserData(state, { payload }: PayloadAction<any>) {
      state.userData = payload;
    },
    addBasketProduct(state, { payload }: PayloadAction<any>) {
      state.basketProduct = payload;
    },
    emptyBasket(state) {
      state.basketProduct = null;
    },
    setLoading(state, { payload }: PayloadAction<boolean>) {
      state.loading = payload;
    },
    setBusinessProfile(state, { payload }: PayloadAction<any>) {
      state.businessProfile = payload;
    },
    setSelectedProduct(state, { payload }: PayloadAction<Product>) {
      state.selectedProduct = payload;
    },
    setAllBusiness(state, { payload }: PayloadAction<string>) {
      state.allBusiness = payload;
    },
    setUserExist(state, { payload }: PayloadAction<boolean>) {
      state.userExist = payload;
    },
    setFirebaseUser(state, { payload }: PayloadAction<any>) {
      state.firebaseUser = payload;
    },
    setUserMembershipStatus(state, { payload }: PayloadAction<any>) {
      state.userMembershipStatus = payload;
    },
    setMembershipUpgraded(state, { payload }: PayloadAction<boolean>) {
      state.membershipUpgraded = payload;
    },
    setIsNextGymSelected(state, { payload }: PayloadAction<boolean>) {
      state.isNextGymSelected = payload;
    },
    setBusinessDashboardData(state, { payload }: PayloadAction<any>) {
      state.businessDashboardData = payload;
    },
    setPartnerGymInfo(state, { payload }: PayloadAction<any>) {
      state.partnerGymInfo = payload;
    },
    setPartnerGyms(state, { payload }: PayloadAction<any[]>) {
      state.partnerGyms = payload;
    },
    setUserCanDowngradeToBasic(state, { payload }: PayloadAction<boolean>) {
      state.userCanDowngradeToBasic = payload;
    },
    setClientSecretStripe(state, { payload }: PayloadAction<string>) {
      state.clientSecretStripe = payload;
    },
    setAppliedDiscount(state, { payload }: PayloadAction<DiscountCode>) {
      state.appliedDiscount = payload;
      state.discountError = null;
    },
    clearAppliedDiscount(state) {
      state.appliedDiscount = null;
      state.discountError = null;
    },
    setDiscountError(state, { payload }: PayloadAction<string>) {
      state.discountError = payload;
      state.appliedDiscount = null;
    },
    setSubscriptionId(state, { payload }: PayloadAction<string>) {
      state.subscriptionId = payload;
    },
    clearClientSecretStripe(state) {
      state.clientSecretStripe = "";
    },
    clearSubscriptionId(state) {
      state.subscriptionId = null;
    },
  },
});

export const {
  setUserData,
  addBasketProduct,
  emptyBasket,
  setLoading,
  setBusinessProfile,
  setSelectedProduct,
  setAllBusiness,
  setUserExist,
  setFirebaseUser,
  setUserMembershipStatus,
  setMembershipUpgraded,
  setIsNextGymSelected,
  setBusinessDashboardData,
  setPartnerGymInfo,
  setPartnerGyms,
  setUserCanDowngradeToBasic,
  setClientSecretStripe,
  setAppliedDiscount,
  clearAppliedDiscount,
  setDiscountError,
  setSubscriptionId,
  clearClientSecretStripe,
  clearSubscriptionId,
} = appData.actions;

export default appData.reducer;
